import { CREATURE_PORTRAITS, PORTRAIT_PLACEHOLDER } from './creatureSprites';
import { PORTRAIT_TONES, placeholderPalette, recolourPlaceholder } from './placeholderColours';

/**
 * The shared placeholder portrait, redrawn in each creature's own colours.
 *
 * The reel is DOM, so a recoloured portrait is a data URL rather than a texture.
 * The base image - a 323-byte file - is decoded once as the app opens, and each
 * creature's version is built on first request and kept, so a spin that cycles
 * through a continent's creatures builds each of them once.
 *
 * A portrait asked for before the base has decoded, or after it failed to decode,
 * falls back to the shared placeholder and says so once in the console. Falling back
 * in silence would put every creature back in the same near-black and read as the
 * colours simply not having been applied, which is the failure this module exists to
 * keep visible.
 */

/** The decoded base portrait's pixels, kept pristine: every creature's colours are applied to a copy. */
let basePixels: ImageData | undefined;

/** The square the base decoded at, which is the canvas every creature's portrait is built on. */
let baseSize = 0;

let ready = false;
let reportedWaiting = false;
let reportedUnmatched = false;

/** Each creature's portrait, built once and reused while the page is open. */
const portraits = new Map<string, string>();

/** Starts decoding the shared portrait. Called once, as the app opens. */
export function startPlaceholderPortraits(): void {
  const image = new Image();
  image.addEventListener('load', () => {
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (width <= 0 || height <= 0) {
      console.warn(`Garaimon: ${PORTRAIT_PLACEHOLDER} decoded with no size, so no creature can be given its own portrait colours.`);
      return;
    }
    if (width !== height) {
      console.warn(`Garaimon: the shared placeholder portrait is ${width}x${height} rather than square, so the reel will draw it squashed.`);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      console.warn('Garaimon: no 2D context is available to recolour the shared placeholder portrait, so no creature can be given its own portrait colours.');
      return;
    }
    context.drawImage(image, 0, 0);
    basePixels = context.getImageData(0, 0, width, height);
    baseSize = width;
    ready = true;
  });
  image.addEventListener('error', () => {
    console.warn(`Garaimon: ${PORTRAIT_PLACEHOLDER} could not be loaded, so every creature falls back to the shared portrait's own colours.`);
  });
  image.src = PORTRAIT_PLACEHOLDER;
}

/**
 * The portrait to draw for a creature.
 *
 * A portrait registered for the species is drawn as it is: real art carries the
 * colour the artist chose, and this module exists only to give the shared
 * placeholder a colour of its own.
 */
export function reelPortraitUrl(speciesId: string): string {
  const own = CREATURE_PORTRAITS[speciesId];
  if (own) return own;
  const built = portraits.get(speciesId);
  if (built) return built;
  const source = basePixels;
  if (!ready || !source) {
    if (!reportedWaiting) {
      reportedWaiting = true;
      console.warn(
        `Garaimon: a portrait of ${speciesId} was asked for before ${PORTRAIT_PLACEHOLDER} had finished decoding, so it is drawn in the shared placeholder's own colours.`
      );
    }
    return PORTRAIT_PLACEHOLDER;
  }
  const canvas = document.createElement('canvas');
  canvas.width = baseSize;
  canvas.height = baseSize;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return PORTRAIT_PLACEHOLDER;
  const image = context.createImageData(baseSize, baseSize);
  image.data.set(source.data);
  const unmatched = recolourPlaceholder(image.data, PORTRAIT_TONES, placeholderPalette(speciesId));
  if (unmatched > 0 && !reportedUnmatched) {
    reportedUnmatched = true;
    console.warn(
      `Garaimon: ${unmatched} opaque pixel(s) of the shared placeholder portrait are in neither of the two tones it is expected to be drawn in, `,
      `so creatures were coloured on the pixels that were recognised. Check PORTRAIT_TONES in src/render/placeholderColours.ts against scripts/prepare_portrait.py.`
    );
  }
  context.putImageData(image, 0, 0);
  const url = canvas.toDataURL('image/png');
  portraits.set(speciesId, url);
  return url;
}
